import { ForfeitLatch, Latch, type FighterInputSource, type FighterIntent } from './fighterInput';
import { aims, applyDeadZone } from './stick';

/**
 * Button indices from the standard mapping (https://w3c.github.io/gamepad/#dfn-standard-gamepad),
 * which names the face buttons by place: the bottom and left ones fire (B and Y on an 8BitDo or
 * Nintendo layout, A and X on an Xbox one), as do the right trigger and bumper; the right and top
 * ones dash (A and X, or B and Y), as do the left trigger and bumper. The owner's layout of
 * 2026-09-22; the triggers keep the pairing a headset player knows.
 */
const ATTACK_BUTTONS = [0, 2, 7, 5];
const DASH_BUTTONS = [1, 3, 6, 4];
/** A trigger is analogue: half-pulled is pulled. */
const TRIGGER_PRESSED = 0.6;

const held = (button: GamepadButton | undefined): boolean =>
  button !== undefined && (button.pressed || button.value > TRIGGER_PRESSED);

const longer = (a: { x: number; z: number }, b: { x: number; z: number }) =>
  Math.hypot(b.x, b.z) > Math.hypot(a.x, a.z) ? b : a;

/**
 * A game controller under the fixed camera: either stick moves the fighter about the arena, and
 * with both pushed the left moves while the right aims, so a fighter can back away facing its
 * foe. The bottom or left face button or the right trigger attacks, the right or top face button
 * or the left trigger dashes. Holding fire borrows the right stick to aim and bend a steerable
 * spellcaster projectile, matching the Quest controls.
 *
 * The sticks are read in screen terms, like the keys are — the camera is square to the grid, so up
 * the screen is +z in the arena and nothing needs rotating. Pushing a stick forward reads negative
 * on its y axis, hence the sign.
 *
 * Polled rather than driven by events, because that is all the Gamepad API offers: the objects it
 * hands back are snapshots and have to be asked for again every frame. Every pad connected is read
 * and the hardest push wins, so it does not matter which port a player's controller is in, and a
 * second pad left plugged in and resting cannot drag the fighter about.
 */
/** Start (button 9) held down gives the duel up (`FORFEIT_HOLD_SECONDS`). */
const FORFEIT_BUTTON = 9;

export function createGamepadFighterInput(
  pads: () => readonly (Gamepad | null)[] = () =>
    typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function'
      ? navigator.getGamepads()
      : [],
): FighterInputSource {
  const dash = new Latch();
  const attack = new Latch();
  let move = { x: 0, z: 0 };
  let aim = { x: 0, z: 0 };
  let attackHeldNow = false;
  let seen = true;
  /** The pads read last frame: the ones in the player's hands, which a pulse is felt in. */
  let inHand: Gamepad[] = [];
  const forfeit = new ForfeitLatch('Start');

  return {
    update(dt) {
      move = { x: 0, z: 0 };
      aim = { x: 0, z: 0 };
      let dashHeld = false;
      let attackHeld = false;
      let startHeld = false;
      seen = false;
      inHand = [];
      for (const pad of pads()) {
        if (!pad?.connected) continue;
        seen = true;
        inHand.push(pad);
        move = longer(move, applyDeadZone(pad.axes[0] ?? 0, -(pad.axes[1] ?? 0)));
        aim = longer(aim, applyDeadZone(pad.axes[2] ?? 0, -(pad.axes[3] ?? 0)));
        if (ATTACK_BUTTONS.some((i) => held(pad.buttons[i]))) attackHeld = true;
        if (DASH_BUTTONS.some((i) => held(pad.buttons[i]))) dashHeld = true;
        if (held(pad.buttons[FORFEIT_BUTTON])) startHeld = true;
      }
      dash.set(dashHeld);
      attack.set(attackHeld);
      attackHeldNow = attackHeld;
      forfeit.set(startHeld, dt);
    },
    next(mode): FighterIntent {
      const magicAim = !!mode?.steerableProjectile && attackHeldNow && !mode.projectileInFlight;
      const magicSteer = !!mode?.steerableProjectile && mode.projectileInFlight && attackHeldNow;
      // Both sticks at once: the left moves and the right aims. One alone still moves and faces,
      // for one hand. The harder push used to win both, so the right stick the hint calls the aim
      // walked the fighter into what it aimed at (client-input:bugs#5).
      const twoSticks = (move.x !== 0 || move.z !== 0) && aims(aim.x, aim.z);
      const walking = magicAim || magicSteer || twoSticks ? move : longer(move, aim);
      const facing = twoSticks ? aim : walking;
      return {
        moveX: walking.x,
        moveZ: walking.z,
        aimAt: null,
        // While fire is held, a mage points the new shot. Once it flies, horizontal stick bends it.
        aimDir:
          magicAim && aims(aim.x, aim.z)
            ? { x: aim.x, z: aim.z }
            : !magicSteer && aims(facing.x, facing.z)
              ? { x: facing.x, z: facing.z }
              : null,
        dash: dash.take(),
        attack: attack.take(),
        steer: magicSteer ? aim.x : 0,
        forfeit: forfeit.take(),
      };
    },
    forfeitHold: () => forfeit.progress(),
    connected: () => seen,
    pulse(strength, seconds) {
      for (const pad of inHand) {
        try {
          void pad.vibrationActuator
            ?.playEffect('dual-rumble', {
              duration: seconds * 1000,
              strongMagnitude: strength,
              weakMagnitude: strength * 0.6,
            })
            .catch(() => undefined);
        } catch {
          // A pad without a motor, or a browser that will not drive it: nothing is felt.
        }
      }
    },
    dispose() {
      dash.reset();
      attack.reset();
      forfeit.reset();
    },
  };
}
